import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 70. TRANSACTIONS
// Les opérations nécessitant plusieurs modifications doivent être
// transactionnelles. Exemple — créer une commande :
//   Order + Order Items + Payment + Inventory Reservation
// Si une étape critique échoue, on évite un état PARTIELLEMENT créé.
// ─────────────────────────────────────────────────────────────

export interface TransactionStep {
  step: string;
  status: 'pending' | 'ok' | 'failed';
  duration_ms: number;
  error?: string | null;
}

export interface CoreTransactionRecord {
  id: number;
  organization_id: number | null;
  tx_id: string;
  name: string | null;
  steps: TransactionStep[] | null;
  step_count: number | null;
  status: string | null;
  error: string | null;
  compensation: unknown;
  duration_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
}

export const TX_STATUS_META: Record<string, { label: string; className: string }> = {
  committed: { label: 'Validée', className: 'bg-secondary-100 text-secondary-900' },
  rolled_back: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
  pending: { label: 'En cours', className: 'bg-primary-100 text-primary-900' },
  partial: { label: 'Partielle', className: 'bg-accent-100 text-accent-900' },
};

export function txStatusMeta(key: string | null | undefined) {
  return TX_STATUS_META[key || 'pending'] || TX_STATUS_META.pending;
}

/** Modèles d’opérations multi-étapes du socle. */
export const TRANSACTION_TEMPLATES: { key: string; label: string; icon: string; steps: string[]; desc: string }[] = [
  {
    key: 'create_order', label: 'Créer une commande', icon: 'ri-shopping-bag-3-line',
    steps: ['Order (entête)', 'Order Items', 'Payment', 'Inventory Reservation'],
    desc: 'Tout ou rien : jamais de commande sans ses lignes ni sa réservation de stock. RPC DB : core_create_order_atomic.',
  },
  {
    key: 'create_invoice', label: 'Créer une facture', icon: 'ri-file-list-3-line',
    steps: ['Invoice', 'Invoice Items', 'Finance Entry'],
    desc: 'La facture et ses lignes sont écrites ensemble.',
  },
  {
    key: 'create_booking', label: 'Créer une réservation', icon: 'ri-calendar-check-line',
    steps: ['Conflict Check', 'Booking', 'Resource Lock', 'Payment'],
    desc: 'Le verrou de ressource n’est posé que si la réservation réussit.',
  },
  {
    key: 'transfer_funds', label: 'Transférer des fonds', icon: 'ri-exchange-dollar-line',
    steps: ['Debit', 'Credit', 'Ledger Entry'],
    desc: 'Un transfert ne laisse jamais un débit sans son crédit.',
  },
];

export function templateMeta(key: string | null | undefined) {
  return TRANSACTION_TEMPLATES.find((t) => t.key === key) || TRANSACTION_TEMPLATES[0];
}

export async function listTransactions(organizationId: number, limit = 60): Promise<CoreTransactionRecord[]> {
  const { data, error } = await supabase
    .from('core_transaction_log')
    .select('id, organization_id, tx_id, name, steps, step_count, status, error, duration_ms, started_at, finished_at')
    .eq('organization_id', organizationId)
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreTransactionRecord[];
}

async function insertTx(payload: Record<string, unknown>): Promise<number | null> {
  const { data, error } = await supabase.from('core_transaction_log').insert(payload).select('id').maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export interface RunTransactionInput<T> {
  tenantId?: number | null;
  organizationId: number;
  name: string;
  steps: string[];
  /** Exécute une étape. Lève une erreur pour déclencher le rollback. */
  run: (step: string, index: number) => Promise<void>;
  /** Compense (annule) une étape déjà réussie lors d’un rollback. */
  compensate?: (step: string, index: number) => Promise<void>;
}

export interface RunTransactionResult {
  ok: boolean;
  txId: string;
  status: 'committed' | 'rolled_back';
  steps: TransactionStep[];
  error?: string;
}

/**
 * Exécute une opération multi-étapes de façon orchestrée.
 * En cas d’échec, compense les étapes déjà réalisées → pas d’état partiel.
 */
export async function runTransaction<T>(input: RunTransactionInput<T>): Promise<RunTransactionResult> {
  const txId = `tx_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
  const started = Date.now();
  const record: TransactionStep[] = [];
  const insertId = await insertTx({
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    tx_id: txId,
    name: input.name,
    steps: [],
    step_count: input.steps.length,
    status: 'pending',
    started_at: new Date(started).toISOString(),
  });

  try {
    for (let i = 0; i < input.steps.length; i += 1) {
      const step = input.steps[i];
      const stepStart = Date.now();
      await input.run(step, i);
      record.push({ step, status: 'ok', duration_ms: Date.now() - stepStart });
    }
    if (insertId) {
      await supabase.from('core_transaction_log').update({
        steps: record, status: 'committed', duration_ms: Date.now() - started, finished_at: new Date().toISOString(),
      }).eq('id', insertId);
    }
    return { ok: true, txId, status: 'committed', steps: record };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Erreur de transaction.';
    record.push({ step: input.steps[record.length] || 'step', status: 'failed', duration_ms: 0, error: message });
    // Compensation : annule les étapes déjà réussies (dans l’ordre inverse).
    if (input.compensate) {
      for (let i = record.filter((r) => r.status === 'ok').length - 1; i >= 0; i -= 1) {
        try {
          await input.compensate(record[i].step, i);
        } catch {
          /* la compensation reste best-effort */
        }
      }
    }
    if (insertId) {
      await supabase.from('core_transaction_log').update({
        steps: record, status: 'rolled_back', error: message, duration_ms: Date.now() - started, finished_at: new Date().toISOString(),
      }).eq('id', insertId);
    }
    return { ok: false, txId, status: 'rolled_back', steps: record, error: message };
  }
}

export interface TransactionStats {
  total: number;
  committed: number;
  rolledBack: number;
  avgDuration: number;
}

export async function fetchTransactionStats(organizationId: number): Promise<TransactionStats> {
  const txs = await listTransactions(organizationId, 100);
  return {
    total: txs.length,
    committed: txs.filter((t) => t.status === 'committed').length,
    rolledBack: txs.filter((t) => t.status === 'rolled_back').length,
    avgDuration: txs.length ? Math.round(txs.reduce((a, t) => a + (t.duration_ms || 0), 0) / txs.length) : 0,
  };
}